/**
 * Overview Tab - Project Settings
 * Sections: Basic Info (name, description, language, status, min duration),
 * plus feature-flagged Number Blocking and Scheduled Email Reports.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_MIN_CALL_DURATION_SECONDS, DEFAULT_MIN_CHAT_MESSAGES } from '@aica/core';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { Switch } from '@/components/ui/switch';
import { ToggleGrid } from '@/components/ui/toggle-grid';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { FieldHelp } from '@/components/help';
import { buildAsrLanguageOptions } from '@/lib/asr-language-options';
import { buildAnalysisLanguageOptions } from '@/lib/analysis-language-options';
import type { ProjectSettingsState, ProjectSettingsActions } from '../types';

interface OverviewTabProps {
  state: ProjectSettingsState;
  actions: ProjectSettingsActions;
}

export function OverviewTab({ state, actions }: OverviewTabProps) {
  const { t, i18n } = useTranslation(['settings', 'projects', 'common']);
  const { isEnabled } = useFeatureFlags();
  // Builds Intl.DisplayNames and runs .of() over all 93 ASR languages plus a
  // sort — recomputing that inline in JSX reran on every keystroke in ANY
  // field on this form (every re-render), not just when the language changed.
  const asrLanguageOptions = useMemo(
    () => buildAsrLanguageOptions(i18n.language, t),
    [i18n.language, t]
  );

  return (
    <div className="space-y-6">
      {/* --- Section: Overview --- */}
      <div className="settings-section">
        <h3 className="text-base font-medium text-fg mb-4">
          {t('settings:overviewTab.title', 'Overview')}
        </h3>

        {/* Project Name */}
        <div className="mb-4">
          <label
            htmlFor="project-name"
            className="block text-sm font-medium text-fg-secondary mb-1.5"
          >
            {t('settings:overviewTab.projectName', 'Project Name')}
          </label>
          <Input
            id="project-name"
            type="text"
            value={state.projectName}
            onChange={(e) => actions.setProjectName(e.target.value)}
            placeholder={t('projects.projectNamePlaceholder')}
          />
        </div>

        {/* Description */}
        <div className="mb-4">
          <label
            htmlFor="project-description"
            className="flex items-center gap-1.5 text-sm font-medium text-fg-secondary mb-1.5"
          >
            {t('settings:overviewTab.description', 'Description')}
            <FieldHelp id="settings.project.description" />
          </label>
          <Textarea
            id="project-description"
            value={state.projectDescription}
            onChange={(e) => actions.setProjectDescription(e.target.value)}
            minRows={3}
            maxLength={1000}
            placeholder={t(
              'settings:overviewTab.descriptionPlaceholder',
              'Project description for AI analysis context'
            )}
          />
          <div className="mt-1 flex items-center justify-between gap-4">
            <p className="text-xs text-fg-muted">
              {t(
                'settings:overviewTab.descriptionHint',
                "Included in every AI analysis of this project's calls"
              )}
            </p>
            <p
              className="shrink-0 text-xs text-fg-subtle"
              data-testid="project-settings-description-count"
            >
              {t('common:common.charactersCount', {
                count: state.projectDescription.length,
                max: 1000,
              })}
            </p>
          </div>
        </div>

        {/* Language */}
        <div className="mb-4">
          <label
            htmlFor="analysisLanguage"
            className="block text-sm font-medium text-fg-secondary mb-1.5"
          >
            {t('settings:overviewTab.analysisLanguage', 'Report language')}
          </label>
          <Select
            id="analysisLanguage"
            value={state.analysisLanguage}
            onValueChange={(val) => actions.setAnalysisLanguage(val as string)}
            // Из каталога ANALYSIS_LANGUAGES: рукописный список здесь молча
            // разошёлся бы с тем, что принимает z.enum на записи.
            options={buildAnalysisLanguageOptions(t)}
            className="max-w-xs"
            size="sm"
          />
          <p className="mt-1.5 text-xs text-fg-muted">
            {t('settings:analysisLanguageHint')}
          </p>
        </div>

        {/* Recognition language */}
        <div className="mb-4">
          <label
            htmlFor="asrLanguage"
            className="block text-sm font-medium text-fg-secondary mb-1.5"
          >
            {t('settings:overviewTab.asrLanguage', 'Recognition language')}
          </label>
          <Select
            id="asrLanguage"
            value={state.asrLanguage}
            onValueChange={(val) => actions.setAsrLanguage(val as string)}
            searchable
            options={asrLanguageOptions}
            className="max-w-xs"
            size="sm"
          />
          <p className="mt-1.5 text-xs text-fg-muted">{t('settings:asrLanguageHint')}</p>
        </div>

        {/* Status */}
        <div className="mb-4">
          <label
            htmlFor="status"
            className="block text-sm font-medium text-fg-secondary mb-1.5"
          >
            {t('settings:overviewTab.status', 'Status')}
          </label>
          <div className="flex items-center gap-2">
            <Select
              id="status"
              value={state.status}
              onValueChange={(val) =>
                actions.setStatus(val as 'active' | 'paused' | 'archived')
              }
              options={[
                {
                  value: 'active',
                  label: t('settings:overviewTab.statusActive', 'Active'),
                },
                {
                  value: 'paused',
                  label: t('settings:overviewTab.statusPaused', 'Paused'),
                },
                {
                  value: 'archived',
                  label: t('settings:overviewTab.statusArchived', 'Archived'),
                },
              ]}
              className="w-40"
              size="sm"
            />
          </div>
        </div>

        {/* Min Call Duration (max-duration and quality-threshold inputs were
            prototype mocks with no persistence — removed) */}
        <div>
          <label
            htmlFor="min-duration"
            className="block text-sm font-medium text-fg-secondary mb-1.5"
          >
            {t('settings:overviewTab.minCallDuration', 'Min Call Duration (sec)')}
          </label>
          <Input
            id="min-duration"
            type="number"
            className="max-w-40"
            value={state.minCallDurationSeconds ?? DEFAULT_MIN_CALL_DURATION_SECONDS}
            onChange={(e) =>
              actions.setMinCallDurationSeconds(Number(e.target.value) || undefined)
            }
          />
        </div>

        {/* Min Chat Messages — Level 2 significance filter for text dialogs */}
        <div>
          <label
            htmlFor="min-chat-messages"
            className="block text-sm font-medium text-fg-secondary mb-1.5"
          >
            {t('settings:overviewTab.minChatMessages', 'Min Chat Messages')}
          </label>
          <Input
            id="min-chat-messages"
            type="number"
            className="max-w-40"
            value={state.minChatMessages ?? DEFAULT_MIN_CHAT_MESSAGES}
            onChange={(e) => {
              // Не `Number(...) || undefined`: 0 — осмысленный режим (схема
              // разрешает его, evaluateChatSignificance его обрабатывает —
              // остаётся одно правило «оператор ответил»), а `||` отправлял
              // его в undefined, значение падало на дефолт 4 и поле на глазах
              // отскакивало назад. Пустое поле по-прежнему = «нет
              // переопределения».
              const raw = e.target.value;
              const parsed = Number(raw);
              actions.setMinChatMessages(
                raw === '' || Number.isNaN(parsed) ? undefined : parsed
              );
            }}
          />
          <p className="text-sm text-fg-muted mt-1.5">
            {t(
              'settings:overviewTab.minChatMessagesDescription',
              'Chats shorter than this — or with no operator reply — keep their transcript but are not analyzed.'
            )}
          </p>
        </div>
      </div>

      {/* --- Section: Number Blocking --- */}
      {isEnabled('enableNumberBlocking') && (
        <div className="border-t border-border pt-4">
          <h3 className="text-base font-medium text-fg mb-1">
            {t('settings:overviewTab.numberBlocking', 'Number Blocking')}
          </h3>
          <p className="text-sm text-fg-muted mb-3">
            {t(
              'settings:overviewTab.numberBlockingDescription',
              "Calls from these numbers won't be analyzed."
            )}
          </p>
          <div className="flex gap-2 mb-2">
            <Input type="text" placeholder="+380 XX XXX XXXX" className="flex-1" />
            <button className="px-3 py-2 text-sm rounded border border-border bg-bg-surface text-fg hover:bg-bg-elevated transition-colors">
              {t('settings:overviewTab.block', 'Block')}
            </button>
          </div>
        </div>
      )}

      {/* --- Section: Scheduled Email Reports --- */}
      {isEnabled('enableScheduledEmailReports') && (
        <div className="border-t border-border pt-4">
          <h3 className="text-base font-medium text-fg mb-3">
            {t('settings:overviewTab.scheduledReports', 'Scheduled Email Reports')}
          </h3>
          <ToggleGrid className="mb-3 gap-2">
            <div className="flex items-center gap-2 px-3 py-2 bg-bg-elevated rounded">
              <span className="text-sm text-fg">
                {t('settings:overviewTab.dailySummary', 'Daily summary')}
              </span>
              <span className="flex-1" />
              <span className="text-xs text-fg-muted">
                {t('settings:overviewTab.everyDayAt', 'Every day at 09:00')}
              </span>
              <Switch defaultChecked size="sm" />
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-bg-elevated rounded">
              <span className="text-sm text-fg">
                {t('settings:overviewTab.weeklyReport', 'Weekly report')}
              </span>
              <span className="flex-1" />
              <span className="text-xs text-fg-muted">
                {t('settings:overviewTab.weekStartAt', 'First day of the week at 08:00')}
              </span>
              <Switch defaultChecked size="sm" />
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-bg-elevated rounded">
              <span className="text-sm text-fg">
                {t('settings:overviewTab.monthlyReport', 'Monthly report')}
              </span>
              <span className="flex-1" />
              <span className="text-xs text-fg-muted">
                {t('settings:overviewTab.firstAt', '1st at 08:00')}
              </span>
              <Switch size="sm" />
            </div>
          </ToggleGrid>
          <div>
            <label
              htmlFor="recipients"
              className="block text-sm font-medium text-fg-secondary mb-1.5"
            >
              {t('settings:overviewTab.recipients', 'Recipients')}
            </label>
            <Input
              id="recipients"
              type="text"
              placeholder={t(
                'settings:overviewTab.recipientsPlaceholder',
                'email@example.com, email2@example.com'
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
}
